// RequestAccessScreen — self-service access form for an unbound Telegram user
// (docs/miniapp-m7-tz.md §3.6). Rendered inline from OrphanScreen (orphan lives
// outside the router). On submit → POST /request-access → success view; the
// orphan autopoll (still running underneath) lands the user in-app once a
// director approves.
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { requestAccess } from '../api/access'
import type { ApiError } from '../api/types'
import { AuthTopBar } from '../components/AuthTopBar'
import { Button, Icon, t, useToast } from '../design'
import { hapticNotify } from '../telegram/tg'

export function RequestAccessScreen({ onBack }: { onBack: () => void }) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [login, setLogin] = useState('')
  const [slug, setSlug] = useState('')
  const [comment, setComment] = useState('')
  const [sent, setSent] = useState(false)
  const [nameErr, setNameErr] = useState(false)

  const mut = useMutation({
    mutationFn: () => requestAccess({
      name: name.trim(),
      bitrix_login_hint: login.trim() || undefined,
      tenant_slug_hint: slug.trim() || undefined,
      comment: comment.trim() || undefined,
    }),
    onSuccess: () => { hapticNotify('success'); setSent(true) },
    onError: (e) => {
      hapticNotify('error')
      const reason = (e as unknown as ApiError)?.reason
      toast(t(reason === 'rate_limited' ? 'req.errRate' : 'req.err'))
    },
  })

  const submit = () => {
    // M-7 §3.8 form validation: surface the error inline on the field
    // (red border + message), not as a transient toast.
    if (name.trim().length < 2) { setNameErr(true); hapticNotify('error'); return }
    setNameErr(false)
    mut.mutate()
  }

  if (sent) {
    return (
      <>
        <AuthTopBar onBack={onBack} />
        <div className="scroll-y" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 16, padding: '20px 24px', textAlign: 'center' }}>
          <span style={{ width: 72, height: 72, borderRadius: 20, background: 'var(--success-bg)', color: 'var(--success)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon.Check size={34} sw={2.5} />
          </span>
          <div className="t-h1">{t('req.sentTitle')}</div>
          <div className="t-caption" style={{ maxWidth: 300, lineHeight: 1.5 }}>{t('req.sentBody')}</div>
          <Button variant="primary" onClick={onBack} style={{ marginTop: 8 }}>{t('req.done')}</Button>
        </div>
      </>
    )
  }

  return (
    <>
      <AuthTopBar onBack={onBack} />
      <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 24px' }}>
        <div className="t-h1" style={{ marginBottom: 12 }}>{t('req.title')}</div>

        <Field
          label={t('req.name')}
          value={name}
          onChange={(v) => { setName(v); if (nameErr) setNameErr(false) }}
          placeholder={t('req.namePh')}
          error={nameErr ? t('req.errName') : undefined}
        />
        <Field label={t('req.login')} value={login} onChange={setLogin} placeholder={t('req.loginPh')} />
        <Field label={t('req.slug')} value={slug} onChange={setSlug} placeholder={t('req.slugPh')} />

        <div style={{ marginBottom: 14 }}>
          <div className="t-label" style={{ marginBottom: 6 }}>{t('req.comment')}</div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('req.commentPh')}
            style={{ width: '100%', minHeight: 64, padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-1)', font: 'inherit', resize: 'vertical', outline: 'none' }}
          />
        </div>

        <Button variant="primary" full disabled={mut.isPending} onClick={submit}>
          {mut.isPending ? t('req.submitting') : t('req.submit')}
        </Button>
      </div>
    </>
  )
}

function Field({
  label, value, onChange, placeholder, error,
}: { label: string; value: string; onChange: (v: string) => void; placeholder: string; error?: string }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="t-label" style={{ marginBottom: 6 }}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${error ? 'var(--danger)' : 'var(--border)'}`, background: 'var(--surface)', color: 'var(--text-1)', font: 'inherit', outline: 'none' }}
      />
      {error && <div className="t-caption" style={{ color: 'var(--danger)', marginTop: 5 }}>{error}</div>}
    </div>
  )
}
